import {
  type AccountStatusInputs,
  accountLookupCollections,
  accountStatusInputsOf,
  accountStatusOf,
  mergeAccountStatus,
  workspaceAccountsWithStatusCollections,
} from "@/models/caching";
import { store } from "@/store/store";
import type { AccountWithWsMembershipAvailability } from "@/store/types";

/** Resolves an account id to its row in the current workspace, or null. */
export type AccountLookup = (accountId: string) => AccountWithWsMembershipAvailability | null;

/**
 * Reads one account's status row, returning the same object while its input rows keep their identities.
 *
 * `useSyncExternalStore` compares snapshots by reference, so a fresh merge on every read would loop.
 */
export class AccountStatusSnapshot {
  private version = -1;
  private inputs: AccountStatusInputs | null = null;
  private value: AccountWithWsMembershipAvailability | null = null;

  public constructor(
    private readonly workspaceId: string | null | undefined,
    private readonly accountId: string | null | undefined,
  ) {}

  /** The current status row; a stable reference until one of its input rows is replaced. */
  public read = (): AccountWithWsMembershipAvailability | null => {
    if (!this.workspaceId || !this.accountId) return null;
    // A flush that wrote none of the four collections cannot have changed the inputs.
    const version = store.versionOf(workspaceAccountsWithStatusCollections);
    if (version === this.version) return this.value;
    this.version = version;
    const inputs = accountStatusInputsOf(this.workspaceId, this.accountId);
    if (sameInputs(this.inputs, inputs)) return this.value;
    this.inputs = inputs;
    this.value = inputs ? mergeAccountStatus(inputs) : null;
    return this.value;
  };
}

/** Whether two input sets hold the same row objects. */
function sameInputs(previous: AccountStatusInputs | null, next: AccountStatusInputs | null): boolean {
  if (previous === null || next === null) return previous === next;
  return (
    previous.account === next.account &&
    previous.membership === next.membership &&
    previous.availability === next.availability &&
    previous.alias === next.alias
  );
}

/** Holds one lookup function per version of the identity collections. */
export class AccountLookupSnapshot {
  private version = -1;
  private lookup: AccountLookup | null = null;

  public constructor(private readonly workspaceId: string | null | undefined) {}

  /** The current lookup; a stable reference until an identity collection is written. */
  public read = (): AccountLookup => {
    const version = store.versionOf(accountLookupCollections);
    if (this.lookup === null || version !== this.version) {
      this.version = version;
      this.lookup = createAccountLookup(this.workspaceId);
    }
    return this.lookup;
  };
}

/** A lookup that merges each account once and answers repeat calls from its cache. */
function createAccountLookup(workspaceId: string | null | undefined): AccountLookup {
  const rows = new Map<string, AccountWithWsMembershipAvailability | null>();
  return (accountId) => {
    if (!workspaceId) return null;
    if (!rows.has(accountId)) rows.set(accountId, accountStatusOf(workspaceId, accountId));
    return rows.get(accountId) ?? null;
  };
}
